// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { UilMessage, UilPlus } from '@iconscout/react-unicons';
import { color, Typography } from '@kintent/glide';
import ReactMarkdown from 'react-markdown';
import { useNavigation } from 'react-navi';

import { GlideButton } from '../../../components/GlideButton';
import { useAIAssistant, useFeatureFlag } from '../../../lib/state';
import { FEATURE_FLAG } from '../../../lib/featureFlags';

// Constants
const LOCAL_STORAGE_KEYS = {
  TRUSTY_CHATS: 'trusty-chats',
  TRUSTY_CURRENT_CHAT: 'trusty-current-chat',
};

const CHAT_DEFAULTS = {
  TITLE: 'New Chat',
  TITLE_MAX_LENGTH: 30,
  TITLE_TRUNCATE_SUFFIX: '...',
};

const UI_TEXT = {
  CHATS_HEADER: 'Chats',
  NEW_CHAT_BUTTON: 'New Chat',
  TRUSTY_HEADER: 'Trusty',
  SOURCES_LABEL: 'Sources:',
  SHOW_BUTTON: 'Show',
  HIDE_BUTTON: 'Hide',
  NO_CONTENT_AVAILABLE: 'No content available',
  INPUT_PLACEHOLDER: 'Type your message...',
  ERROR_MESSAGE: 'Sorry, I encountered an error. Please try again.',
};

const MESSAGE_KEYS = {
  USER_PREFIX: 'user',
  ASSISTANT_PREFIX: 'assistant',
  MESSAGE_KEY_PREFIX: 'message-',
  CITATION_KEY_PREFIX: 'citation-',
};

// Citations Component
function Citations({ citations, messageKey, expandedCitations, toggleCitation }) {
  return (
    <CitationContainer>
      <CitationHeader isExpanded={expandedCitations[messageKey]}>
        <Typography
          level="8"
          as="p"
        >
          {UI_TEXT.SOURCES_LABEL} {citations.length}
        </Typography>
        <GlideButton
          variant="ghost"
          onClick={() => toggleCitation(messageKey)}
        >
          {expandedCitations[messageKey] ? UI_TEXT.HIDE_BUTTON : UI_TEXT.SHOW_BUTTON}
        </GlideButton>
      </CitationHeader>
      <CitationList isExpanded={expandedCitations[messageKey]}>
        {citations.map((citation) => {
          // Create a safe unique key for citations
          const citationTextHash = (citation.text || '').split('').reduce((hash, char) => {
            return (hash * 31 + char.charCodeAt(0)) % 1000000;
          }, 0);
          return (
            <CitationItem
              key={`${MESSAGE_KEYS.CITATION_KEY_PREFIX}${citation.id || citation.type}-${citationTextHash}`}
            >
              <Typography
                level="9"
                as="p"
                style={{ fontWeight: 500 }}
              >
                {citation.type}
              </Typography>
              <Typography
                level="9"
                as="p"
                style={{ marginTop: '4px' }}
              >
                {citation.text || citation.title || UI_TEXT.NO_CONTENT_AVAILABLE}
              </Typography>
              {citation.url && (
                <Typography
                  level="9"
                  as="p"
                  style={{ color: '#666', marginTop: '4px' }}
                >
                  {citation.url}
                </Typography>
              )}
            </CitationItem>
          );
        })}
      </CitationList>
    </CitationContainer>
  );
}

const Layout = styled.div`
  display: flex;
  height: calc(100vh - 160px);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  gap: 20px;
`;

const SidePanel = styled.div`
  width: 250px;
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  box-sizing: border-box;
`;

const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
`;

const ChatItem = styled.div`
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ isActive, theme }) => (isActive ? color(theme.colorV2.system.positive, 100) : 'transparent')};
  border: 1px solid
    ${({ isActive, theme }) =>
      isActive ? theme.colorV2.system.positive[600] : color(theme.colorV2.system.border, 200)};

  &:hover {
    background-color: ${({ isActive, theme }) =>
      isActive ? color(theme.colorV2.system.positive, 100) : color(theme.colorV2.system.gray, 100)};
  }
`;

const NewChatButton = styled(GlideButton)`
  width: 100%;
  justify-content: center;
  gap: 8px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const ChatContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  margin-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: calc(100% - 80px);
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const MessageContainer = styled.div`
  padding: 20px;
  border-radius: 12px;
  background-color: ${({ isUser, theme }) =>
    isUser ? color(theme.colorV2.system.positive, 100) : color(theme.colorV2.system.gray, 100)};
  max-width: 85%;
  ${({ isUser }) => (isUser ? 'margin-left: auto;' : 'margin-right: auto;')}
  box-shadow: 0 1px 2px ${({ theme }) => color(theme.colorV2.system.gray, 100)};
`;

const InputContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background-color: white;
  border-top: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 200)};
  display: flex;
  gap: 10px;
  height: 80px;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
`;

const StyledTextInput = styled.input`
  height: 40px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 200)};
  border-radius: 8px;
  flex: 1;
  min-width: 0;
  font-size: 14px;

  &:focus {
    border-color: ${({ theme }) => theme.colorV2.system.positive[600]};
    outline: none;
  }
`;

const CitationContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: ${({ theme }) => color(theme.colorV2.system.gray, 50)};
  border-radius: 8px;
  font-size: 0.9em;
  border: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 200)};
`;

const CitationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ isExpanded }) => (isExpanded ? '12px' : '0')};
`;

const CitationList = styled.div`
  display: ${({ isExpanded }) => (isExpanded ? 'block' : 'none')};
`;

const CitationItem = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => color(theme.colorV2.system.border, 200)};

  &:last-child {
    border-bottom: none;
  }
`;

const MessageText = styled.div`
  white-space: pre-wrap;
  line-height: 1.5;

  p {
    margin: 0 0 0.75em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0.25em 0;
    padding-left: 1.5em;

    li {
      margin: 0.25em 0;
    }
  }

  code {
    background-color: ${({ theme }) => color(theme.colorV2.system.gray, 100)};
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
  }

  pre {
    background-color: ${({ theme }) => color(theme.colorV2.system.gray, 100)};
    padding: 1em;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5em 0;

    code {
      background-color: transparent;
      padding: 0;
    }
  }
`;

export default function Trusty() {
  const navigation = useNavigation();
  const isTrustyEnabled = useFeatureFlag(FEATURE_FLAG.TRUSTY_ENABLED);
  const { sendMessage, isLoading: isApiLoading } = useAIAssistant();

  useEffect(() => {
    if (!isTrustyEnabled) {
      navigation.navigate('/home');
    }
  }, [isTrustyEnabled, navigation]);

  if (!isTrustyEnabled) {
    return null;
  }

  const [chats, setChats] = useState(() => {
    const savedChats = localStorage.getItem(LOCAL_STORAGE_KEYS.TRUSTY_CHATS);
    if (savedChats) {
      return JSON.parse(savedChats);
    }
    // Initialize with a default chat
    const defaultChat = {
      id: Date.now().toString(),
      title: CHAT_DEFAULTS.TITLE,
      messages: [],
    };
    return [defaultChat];
  });

  const [currentChatId, setCurrentChatId] = useState(() => {
    const savedCurrentChat = localStorage.getItem(LOCAL_STORAGE_KEYS.TRUSTY_CURRENT_CHAT);
    return savedCurrentChat || chats[0].id;
  });

  const [expandedCitations, setExpandedCitations] = useState({});
  const [inputMessage, setInputMessage] = useState('');
  const chatContainerRef = useRef(null);

  // Save chats to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.TRUSTY_CHATS, JSON.stringify(chats));
    localStorage.setItem(LOCAL_STORAGE_KEYS.TRUSTY_CURRENT_CHAT, currentChatId);
  }, [chats, currentChatId]);

  const currentChat = chats.find((chat) => chat.id === currentChatId) || chats[0];

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentChat.messages]);

  const createNewChat = () => {
    const newChat = {
      id: Date.now().toString(),
      title: CHAT_DEFAULTS.TITLE,
      messages: [],
    };
    setChats((prev) => [...prev, newChat]);
    setCurrentChatId(newChat.id);
  };

  const updateChatTitle = (chatId, firstMessage) => {
    setChats((prev) =>
      prev.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            title:
              firstMessage.slice(0, CHAT_DEFAULTS.TITLE_MAX_LENGTH) +
              (firstMessage.length > CHAT_DEFAULTS.TITLE_MAX_LENGTH ? CHAT_DEFAULTS.TITLE_TRUNCATE_SUFFIX : ''),
          };
        }
        return chat;
      })
    );
  };

  const handleSubmit = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = { text: inputMessage, isUser: true };

    // Update chat title if this is the first message
    if (currentChat.messages.length === 0) {
      updateChatTitle(currentChatId, inputMessage);
    }

    setChats((prev) =>
      prev.map((chat) => {
        if (chat.id === currentChatId) {
          return {
            ...chat,
            messages: [...chat.messages, userMessage],
          };
        }
        return chat;
      })
    );

    try {
      const response = await sendMessage(inputMessage);

      const assistantMessage = {
        text: response.response,
        isUser: false,
        citations: response.citations || [],
      };

      setChats((prev) =>
        prev.map((chat) => {
          if (chat.id === currentChatId) {
            return {
              ...chat,
              messages: [...chat.messages, assistantMessage],
            };
          }
          return chat;
        })
      );
    } catch (error) {
      const errorMessage = {
        text: UI_TEXT.ERROR_MESSAGE,
        isUser: false,
        citations: [],
      };

      setChats((prev) =>
        prev.map((chat) => {
          if (chat.id === currentChatId) {
            return {
              ...chat,
              messages: [...chat.messages, errorMessage],
            };
          }
          return chat;
        })
      );
    } finally {
      setInputMessage('');
    }
  };

  const toggleCitation = (index) => {
    setExpandedCitations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Layout>
      <SidePanel>
        <Typography
          level="4"
          as="h2"
        >
          {UI_TEXT.CHATS_HEADER}
        </Typography>
        <NewChatButton onClick={createNewChat}>
          <UilPlus size={18} />
          {UI_TEXT.NEW_CHAT_BUTTON}
        </NewChatButton>
        <ChatList>
          {chats.map((chat) => (
            <ChatItem
              key={chat.id}
              isActive={chat.id === currentChatId}
              onClick={() => setCurrentChatId(chat.id)}
            >
              <Typography
                level="8"
                as="p"
              >
                {chat.title}
              </Typography>
            </ChatItem>
          ))}
        </ChatList>
      </SidePanel>
      <MainContent>
        <Container>
          <Typography
            level="4"
            as="h1"
          >
            {UI_TEXT.TRUSTY_HEADER}
          </Typography>
          <ChatContainer ref={chatContainerRef}>
            {currentChat.messages.map((message) => {
              // Create a safe unique key using message properties and a hash of the text
              const textHash = (message.text || '').split('').reduce((hash, char) => {
                return (hash * 31 + char.charCodeAt(0)) % 1000000;
              }, 0);
              const messageKey = `${MESSAGE_KEYS.MESSAGE_KEY_PREFIX}${
                message.isUser ? MESSAGE_KEYS.USER_PREFIX : MESSAGE_KEYS.ASSISTANT_PREFIX
              }-${textHash}`;

              return (
                <MessageContainer
                  key={messageKey}
                  isUser={message.isUser}
                >
                  <MessageText>
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </MessageText>
                  {message.citations && message.citations.length > 0 && (
                    <Citations
                      citations={message.citations}
                      messageKey={messageKey}
                      expandedCitations={expandedCitations}
                      toggleCitation={toggleCitation}
                    />
                  )}
                </MessageContainer>
              );
            })}
          </ChatContainer>
          <InputContainer>
            <StyledTextInput
              name="message"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={UI_TEXT.INPUT_PLACEHOLDER}
              disabled={isApiLoading}
            />
            <GlideButton
              onClick={handleSubmit}
              disabled={isApiLoading || !inputMessage.trim()}
            >
              <UilMessage size={18} />
            </GlideButton>
          </InputContainer>
        </Container>
      </MainContent>
    </Layout>
  );
}
